const CALLBACK_PATH = '/oauth/callback/google';

/** RFC1918 + link-local — Google OAuth web clients reject these as redirect_uri. */
export function isPrivateNetworkHostname(hostname: string): boolean {
  const h = hostname.trim().toLowerCase();
  if (!h || h === 'localhost' || h === '127.0.0.1' || h === '::1') return false;

  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  const m172 = /^172\.(\d{1,3})\.\d{1,3}\.\d{1,3}$/.exec(h);
  if (m172) {
    const second = Number(m172[1]);
    if (second >= 16 && second <= 31) return true;
  }
  if (/^169\.254\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  if (h.endsWith('.local')) return true;
  return false;
}

/**
 * When set, user sees this instead of being sent to Google (which returns 400 invalid_request).
 */
export function getGoogleOAuthBlockedReason(): string | null {
  const fromEnv = import.meta.env.VITE_GOOGLE_REDIRECT_URI;
  if (typeof fromEnv === 'string' && fromEnv.trim() !== '') {
    return null;
  }
  if (typeof window === 'undefined') return null;

  const host = window.location.hostname;
  if (!isPrivateNetworkHostname(host)) return null;

  return (
    'Google Sign-In cannot use a LAN address (such as ' +
    `${window.location.host}) as the OAuth callback. ` +
    'On your phone, open the public booking URL (for example https://book.teboxapp.com/your-club), ' +
    'or for local dev expose port 5175 with an HTTPS tunnel (ngrok) and set ' +
    'VITE_GOOGLE_REDIRECT_URI to that callback URL in apps/booking/.env.local.'
  );
}

/**
 * `redirect_uri` for Google authorize + API token exchange — must match Google Cloud
 * "Authorized redirect URIs" and API `GOOGLE_REDIRECT_URI` exactly (no trailing slash).
 *
 * If `VITE_GOOGLE_REDIRECT_URI` is set to an origin only (common misconfiguration),
 * the callback path is appended automatically.
 */
export function getGoogleOAuthRedirectUri(): string {
  const fromEnv = import.meta.env.VITE_GOOGLE_REDIRECT_URI;
  if (typeof fromEnv === 'string' && fromEnv.trim() !== '') {
    return normalizeConfiguredRedirect(fromEnv.trim());
  }
  const origin = window.location.origin.replace(/\/$/, '');
  return `${origin}${CALLBACK_PATH}`;
}

function normalizeConfiguredRedirect(raw: string): string {
  try {
    const u = new URL(raw);
    const path = u.pathname.replace(/\/+$/, '') || '/';
    if (path === '/' || path === '') {
      u.pathname = CALLBACK_PATH;
    }
    return `${u.origin}${u.pathname}`.replace(/\/+$/, '');
  } catch {
    const base = raw.replace(/\/+$/, '');
    return `${base}${CALLBACK_PATH}`;
  }
}
